import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateStudentAffairsDocument } from '@/api/studentAffairs.api';
import { ApiError } from '@/api/errors';
import { Stepper } from '@/components/form/Stepper';
import { StatusChip } from '@/components/common/StatusChip';
import {
  isCopiesCountEditable,
  isCopiesCountRequired,
  isNotesRequired,
  toneForDocumentStatus,
} from '@/lib/documentStatus';
import { DOCUMENT_RECEIVED_STATUSES } from '@/types/shared';
import type { DocumentChecklistRow, DocumentReceivedStatus } from '@/types/shared';
import './DocumentRow.css';

interface LocalState {
  receivedStatus: DocumentReceivedStatus;
  copiesCount: number;
  receivedDate: string;
  notes: string;
}

interface FieldErrors {
  copiesCount?: string;
  notes?: string;
}

function toLocal(row: DocumentChecklistRow): LocalState {
  return {
    receivedStatus: row.receivedStatus,
    copiesCount: row.copiesCount,
    receivedDate: row.receivedDate ? row.receivedDate.slice(0, 10) : '',
    notes: row.notes ?? '',
  };
}

function isSameState(a: LocalState, b: LocalState): boolean {
  return (
    a.receivedStatus === b.receivedStatus &&
    a.copiesCount === b.copiesCount &&
    a.receivedDate === b.receivedDate &&
    a.notes === b.notes
  );
}

interface DocumentRowProps {
  studentCode: string;
  row: DocumentChecklistRow;
  canEdit: boolean;
  onSaved: (updated: DocumentChecklistRow) => void;
}

/** One editable row of the 10-item document checklist — 08-staff-ux.md Student Affairs flow.
 * Saves independently per row via `PATCH .../documents/:documentType` so an officer can work
 * through the checklist item-by-item without a page-wide save/discard step. */
export function DocumentRow({ studentCode, row, canEdit, onSaved }: DocumentRowProps) {
  const { t } = useTranslation();
  const baseId = `doc-${row.documentType}`;

  const [local, setLocal] = useState<LocalState>(() => toLocal(row));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [rowError, setRowError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Resync local edit state whenever the source row changes (initial load, or after this row's
  // own successful save produced a fresh server copy) — never while another row's save is
  // in-flight, since only this row's `row` prop reference changes on its own update.
  useEffect(() => {
    setLocal(toLocal(row));
  }, [row]);

  const dirty = !isSameState(local, toLocal(row));
  const copiesEditable = isCopiesCountEditable(local.receivedStatus);

  function handleStatusChange(next: DocumentReceivedStatus) {
    setRowError(null);
    setFieldErrors({});
    setSavedFlash(false);
    setLocal((prev) => {
      // G5: reverting to "Not Received" clears copies/received date immediately in the UI too.
      if (next === 'Not Received') {
        return { ...prev, receivedStatus: next, copiesCount: 0, receivedDate: '' };
      }
      return { ...prev, receivedStatus: next };
    });
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (isCopiesCountRequired(local.receivedStatus) && local.copiesCount < 1) {
      errors.copiesCount = t('studentAffairs.documents.errors.copiesRequired');
    }
    if (isNotesRequired(local.receivedStatus) && !local.notes.trim()) {
      errors.notes = t('studentAffairs.documents.errors.notesRequired');
    }
    return errors;
  }

  async function handleSave() {
    setRowError(null);
    setSavedFlash(false);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const updated = await updateStudentAffairsDocument(studentCode, row.documentType, {
        receivedStatus: local.receivedStatus,
        copiesCount: local.copiesCount,
        receivedDate: local.receivedDate || null,
        notes: local.notes.trim() || null,
        // T133 (§17, DEC-DOC1 = Option A): the per-document `link` input is removed from the UI —
        // replaced by one shared, student-level `documentsLink` field on the detail page. The
        // per-document `link` value on the server is intentionally left untouched (omitted here,
        // not sent as `null`) since it is deprecated-but-retained, not cleared (AC-6).
      });
      onSaved(updated);
      setFieldErrors({});
      setSavedFlash(true);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: FieldErrors = {};
      for (const detail of apiError.details) {
        if (detail.field === 'copiesCount') errs.copiesCount = detail.message;
        if (detail.field === 'notes') errs.notes = detail.message;
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
      } else {
        setRowError(apiError.message || t('studentAffairs.documents.errors.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fa-doc-row">
      <div className="fa-doc-row__field fa-doc-row__field--label">
        <div className="fa-doc-row__name-line">
          <span className="fa-doc-row__label-text">
            {t(`studentAffairs.documents.documentTypes.${row.documentType}`)}
          </span>
          {!canEdit && (
            <StatusChip
              label={t(`studentAffairs.documents.status.${row.receivedStatus}`)}
              tone={toneForDocumentStatus(row.receivedStatus)}
            />
          )}
        </div>
      </div>

      {canEdit ? (
        <>
          <div className="fa-doc-row__field">
            <label className="fa-doc-row__field-label" htmlFor={`${baseId}-status`}>
              {t('studentAffairs.documents.columns.status')}
            </label>
            <select
              id={`${baseId}-status`}
              value={local.receivedStatus}
              disabled={saving}
              onChange={(e) => handleStatusChange(e.target.value as DocumentReceivedStatus)}
            >
              {DOCUMENT_RECEIVED_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`studentAffairs.documents.status.${status}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="fa-doc-row__field">
            <span className="fa-doc-row__field-label" id={`${baseId}-copies-label`}>
              {t('studentAffairs.documents.columns.copies')}
            </span>
            <Stepper
              id={`${baseId}-copies`}
              value={local.copiesCount}
              min={0}
              onChange={(v) => setLocal((prev) => ({ ...prev, copiesCount: v === '' ? 0 : v }))}
              decrementLabel={t('studentAffairs.documents.decreaseCopies')}
              incrementLabel={t('studentAffairs.documents.increaseCopies')}
            />
            {!copiesEditable && (
              <p className="fa-doc-row__hint">{t('studentAffairs.documents.copiesDisabledHint')}</p>
            )}
            {fieldErrors.copiesCount && (
              <p className="fa-field__error">{fieldErrors.copiesCount}</p>
            )}
          </div>

          <div className="fa-doc-row__field">
            <label className="fa-doc-row__field-label" htmlFor={`${baseId}-date`}>
              {t('studentAffairs.documents.columns.receivedDate')}
            </label>
            <input
              id={`${baseId}-date`}
              type="date"
              value={local.receivedDate}
              disabled={saving || !copiesEditable}
              onChange={(e) => setLocal((prev) => ({ ...prev, receivedDate: e.target.value }))}
            />
          </div>

          <div className="fa-doc-row__field fa-doc-row__field--notes">
            <label className="fa-doc-row__field-label" htmlFor={`${baseId}-notes`}>
              {t('studentAffairs.documents.columns.notes')}
              {isNotesRequired(local.receivedStatus) && (
                <span className="fa-field__required" aria-hidden="true">
                  {' '}
                  *
                </span>
              )}
            </label>
            <textarea
              id={`${baseId}-notes`}
              value={local.notes}
              disabled={saving}
              aria-invalid={Boolean(fieldErrors.notes)}
              onChange={(e) => setLocal((prev) => ({ ...prev, notes: e.target.value }))}
            />
            {fieldErrors.notes && <p className="fa-field__error">{fieldErrors.notes}</p>}
          </div>

          <div className="fa-doc-row__field fa-doc-row__field--actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary fa-doc-row__save"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? t('studentAffairs.documents.saving') : t('common.save')}
            </button>
            {savedFlash && !dirty && (
              <span className="fa-doc-row__saved" role="status">
                {t('studentAffairs.documents.saved')}
              </span>
            )}
            {rowError && (
              <p className="fa-field__error" role="alert">
                {rowError}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="fa-doc-row__field">
            <span className="fa-doc-row__field-label">
              {t('studentAffairs.documents.columns.copies')}
            </span>
            <span className="fa-ltr-isolate">{row.copiesCount}</span>
          </div>
          <div className="fa-doc-row__field">
            <span className="fa-doc-row__field-label">
              {t('studentAffairs.documents.columns.receivedDate')}
            </span>
            <span className="fa-ltr-isolate">{row.receivedDate ?? '—'}</span>
          </div>
          <div className="fa-doc-row__field fa-doc-row__field--notes">
            <span className="fa-doc-row__field-label">
              {t('studentAffairs.documents.columns.notes')}
            </span>
            <span>{row.notes || '—'}</span>
          </div>
        </>
      )}
    </div>
  );
}
